import { tradeApi } from "@/api/exchange.page";
import { marketApi } from "@/api/marketApi";
import { BigNumber } from "bignumber.js";

export default {
    name: "DataProvider",
    mounted() {
        const socket = this.$socket.sock();
        socket.on("ping", (value) => {
            if (!this.provider.ping) {
                this.provider.ping = true;
            }
        });

        socket.on("pong", (value) => {
            if (!this.provider.pong) {
                this.provider.pong = true;
            }
        });
    },
    watch: {
        //must wait until api gives data only sync
        'provider.websocket.buy'(value) {
            if (!this.provider.lockSync && this.provider.apiReady) {
                this.sync();
            } else {
                this.provider.requireSync = true;
            }
        },
        'provider.websocket.sell'(value) {
            if (!this.provider.lockSync && this.provider.apiReady) {
                this.sync();
            } else {
                this.provider.requireSync = true;
            }
        },
        'provider.websocket.kline'(value) {
            if (!this.provider.lockSync && this.provider.apiReady) {
                this.sync();
            } else {
                this.provider.requireSync = true;
            }
        },
        'provider.websocket.tradeHistory'(value) {
            if (!this.provider.lockSync && this.provider.apiReady) {
                this.sync();
            } else {
                this.provider.requireSync = true;
            }
        },
        'provider.buy'(value) {
            this.getBestSell();
        },
        'provider.sell'(value) {
            this.getBestBuy();
        },
        'provider.data.buy'(value) {
            if (this.provider.apiReady) {
                if (0 < value.length) {
                    const sorted = [...value];

                    if (this.provider.maxDepthRow >= sorted.length || !this.latestTradeLowerLimit) {
                        this.provider.buyChart = this.setDepthChart([...sorted]).reverse();
                    } else {
                        const filtered = sorted.filter(item => {
                            const price = new BigNumber(item.price);
                            if (price.isGreaterThanOrEqualTo(this.latestTradeLowerLimit)) {
                                return item;
                            }
                        });
                        this.provider.buyChart = this.setDepthChart([...filtered]).reverse();
                    }
                } else {
                    this.provider.buyChart = [];
                }
            }
        },
        'provider.data.sell'(value) {
            if (this.provider.apiReady) {
                if (0 < value.length) {
                    const sorted = [...value];

                    if (this.provider.maxDepthRow >= sorted.length || !this.latestTradeUpperLimit) {
                        this.provider.sellChart = this.setDepthChart([...sorted]);
                    } else {
                        const filtered = sorted.filter(item => {
                            const price = new BigNumber(item.price);
                            if (price.isLessThanOrEqualTo(this.latestTradeUpperLimit)) {
                                return item;
                            }
                        });
                        this.provider.sellChart = this.setDepthChart([...filtered]);
                    }
                } else {
                    this.provider.sellChart = [];
                }
            }
        },
        'provider.model.activePair'(value) {
            for (let group in this.provider.marketList) {
                const item = this.provider.marketList[group][value];
                if (item) {
                    this.provider.model.activePairGroup = group;
                    return;
                }
            }
        },
        'provider.ping'(value) {
            if (value) {
                setTimeout(() => {
                    this.provider.ping = false;
                }, 500)
            }
        },
        'provider.pong'(value) {
            if (value) {
                setTimeout(() => {
                    this.provider.pong = false;
                }, 500)
            }
        }
    },
    computed: {
        realTimeKlineData() {
            let response = this.syncKline([...this.provider.queue.kline], []);
            if (0 < response.length) {
                for (let i = 0; i < response.length; i++) {
                    response[i].time = response[i].timestamp;
                }
            }
            return response;
        },
        latestTrade() {
            const response = (0 >= this.provider.tradeHistory.length ? null : this.provider.tradeHistory[0]);
            return response;
        },
        currentMarket() {
            const response = this.provider.marketList[this.provider.model.activePairGroup][this.provider.model.activePair];
            return response;
        },
        latestTradeUpperLimit() {
            if (!this.latestTrade) {
                return 0;
            }

            const price = new BigNumber(this.latestTrade.price);
            return price.times(!window.env.depthGraphSellPriceOffset ? 2 : window.env.depthGraphSellPriceOffset);
        },
        latestTradeLowerLimit() {
            if (!this.latestTrade) {
                return 0;
            }

            const price = new BigNumber(this.latestTrade.price);
            return price.times(!window.env.depthGraphBuyPriceOffset ? 0.5 : window.env.depthGraphBuyPriceOffset);
        },
    },
    methods: {
        //api methods
        getMarketList() {
            return marketApi.getMarketList().then(response => {
                this.provider.marketList = {};
                this.provider.marketListGroup = [];

                if (0 == response.errcode) {
                    for (let i = 0; i < response.data.length; i++) {
                        const item = response.data[i];

                        if (-1 == this.provider.marketListGroup.indexOf(item.marketCoin)) {
                            this.provider.marketListGroup.push(item.marketCoin);
                        }

                        // use set to add reactivity, can check vue js reactivity in depth
                        if (!this.provider.marketList[item.marketCoin]) {
                            // this.provider.marketList[item.marketCoin] = {};
                            this.$set(this.provider.marketList, item.marketCoin, {});
                        }

                        if (!this.provider.marketList[item.marketCoin][item.symbol]) {
                            // this.provider.marketList[item.marketCoin][item.symbol] = item;
                            this.$set(this.provider.marketList[item.marketCoin], item.symbol, item);
                        }

                        if (!this.provider.activePair && 1 == item.tradeStatus && 1 == item.status) {
                            this.provider.activePair = item;
                            this.provider.activePairGroup = item.marketCoin;
                        }
                    }

                    return;
                }

                throw response.errmsg;
            }).catch(error => {
                throw error;
            });
        },
        getExchangeData(pair, depthGroup, interval) {
            const params = {
                pair,
                depthGroup,
                interval,
                filter: '011',
            };

            return this.getTradeList(params).then((response) => {
                this.provider.model.activePair = pair;
                this.provider.model.activeDepth = depthGroup;
                this.provider.model.activeInterval = interval;

                if (response.kline) {
                    const data = this.onKline(response.kline);
                    this.provider.data.kline = data;
                    this.provider.kline = data;
                }

                if (response.depth) {
                    if (response.depth.buy) {
                        const data = this.onDepth(response.depth.buy);
                        const sorted = this.sortBuyDepth([...data]);
                        this.provider.data.buy = [...sorted];
                        this.provider.buy = this.limitBuyDepth([...sorted]);
                    }

                    if (response.depth.sell) {
                        const data = this.onDepth(response.depth.sell);
                        const sorted = this.sortSellDepth([...data]);
                        this.provider.data.sell = [...sorted];
                        this.provider.sell = this.limitSellDepth([...sorted]);
                    }
                }

                if (response.tradeHistory) {
                    const data = response.tradeHistory.slice(0, this.provider.maxTradeHistoryRow);
                    this.provider.data.tradeHistory = data;
                    this.provider.tradeHistory = data;
                }

                this.provider.apiReady = true;

                return this.sync();
            });
        },
        getExchangeDataByDepth(pair, depthGroup) {
            const params = {
                pair,
                depthGroup,
                filter: '010'
            }

            return this.getTradeList(params).then((response) => {
                this.provider.model.activeDepth = depthGroup;

                if (response.depth) {
                    if (response.depth.buy) {
                        const data = this.onDepth(response.depth.buy);
                        const sorted = this.sortBuyDepth([...data]);
                        this.provider.data.buy = [...sorted];
                        this.provider.buy = this.limitBuyDepth([...sorted]);
                    }

                    if (response.depth.sell) {
                        const data = this.onDepth(response.depth.sell);
                        const sorted = this.sortSellDepth([...data]);
                        this.provider.data.sell = [...sorted];
                        this.provider.sell = this.limitSellDepth([...sorted]);
                    }
                }

                this.provider.apiReady = true;

                return this.sync();
            });
        },
        getExchangeDataByInterval(pair, interval) {
            const params = {
                pair,
                interval,
                filter: '100',
            }

            return this.getTradeList(params).then((response) => {
                this.provider.model.activeInterval = interval;

                if (response.kline) {
                    const data = this.onKline(response.kline);
                    this.provider.data.kline = data;
                    this.provider.kline = data;
                }

                this.provider.apiReady = true;

                return this.sync();
            });
        },
        getTradeList(params) {
            return tradeApi.getTradeList(params).then(response => {
                if (0 == response.errcode) {
                    return response.data;
                }

                throw response.errmsg;
            }).catch(error => {
                throw error;
            });
        },


        //websocket methods
        getRealTimeExchangeData(pair, depthGroup, interval) {
            const self = this;

            this.$socket.subscribe("marketList", message => {
                if (0 == message.errcode) {
                    this.onMarketListWebsocketCallback(message.data);
                    return
                }
            });

            //subsribe to setlist & get list
            return this.$socket.emit('setList', {
                pair,
                depthGroup,
                interval,
            }).then(() => {
                return self.$socket.subscribe("getList", message => {
                    if (0 == message.errcode) {
                        self.onExchangeDataWebsocketCallback(message.data);
                        return;
                    }

                    throw message.errmsg;
                });
            }).catch(error => {
                throw error;
            });
        },
        onMarketListWebsocketCallback(data) {
            if (-1 == this.provider.marketListGroup.indexOf(data.marketCoin)) {
                this.provider.marketListGroup.push(data.marketCoin);
            }

            // use set to add reactivity, can check vue js reactivity in depth
            if (!this.provider.marketList[data.marketCoin]) {
                // this.provider.marketList[data.marketCoin] = {};
                this.$set(this.provider.marketList, data.marketCoin, {});
            }

            if (!this.provider.marketList[data.marketCoin][data.symbol]) {
                this.$set(this.provider.marketList[data.marketCoin], data.symbol, data);
            } else {
                this.provider.marketList[data.marketCoin][data.symbol] = data;
            }
        },
        onExchangeDataWebsocketCallback(data) {
            // we use unshift so the latest data will always be on the top of the array
            // if (data.pair == this.provider.model.activePair && data.depthGroup == this.provider.model.activeDepth && data.interval == this.provider.model.activeInterval) {
            if (data.kline) {
                for (let i = 0; i < data.kline.length; i++) {
                    this.provider.websocket.kline.unshift(data.kline[i]);
                }
            }

            if (data.depth) {
                if (data.depth.buy) {
                    for (let i = 0; i < data.depth.buy.length; i++) {
                        this.provider.websocket.buy.unshift(data.depth.buy[i]);
                    }
                }

                if (data.depth.sell) {
                    for (let i = 0; i < data.depth.sell.length; i++) {
                        this.provider.websocket.sell.unshift(data.depth.sell[i]);
                    }
                }
            }

            if (data.tradeHistory) {
                for (let i = 0; i < data.tradeHistory.length; i++) {
                    this.provider.websocket.tradeHistory.unshift(data.tradeHistory[i]);
                }
            }
            // }

            return;
        },

        //misc
        async watch(pair, depthGroup, interval) {
            this.provider.recoveryMode = true;
            this.provider.apiReady = false;

            if (this.provider.requireSync) {
                this.$socket.unsubscribe("getList");
                this.clearQueue();
                setTimeout(() => {
                    this.provider.requireSync = false;
                    this.watch(pair, depthGroup, interval);
                }, 500);
                return;
            }

            //clear any existing queue first
            this.clearQueue();

            let init = true;
            if (!this.provider.model.activePair && !this.provider.model.activeDepth && !this.provider.model.activeInterval) {
                this.provider.model.activePair = pair;
                this.provider.model.activeDepth = depthGroup;
                this.provider.model.activeInterval = interval;
            } else {
                init = false;
            }

            this.getRealTimeExchangeData(pair, depthGroup, interval);

            if (init) {
                await this.getExchangeData(pair, depthGroup, interval);
            } else {
                if (depthGroup != this.provider.model.activeDepth && null == pair && null == interval) {
                    await this.getExchangeDataByDepth(this.provider.model.activePair, depthGroup);
                    return;
                }

                if (interval != this.provider.model.activeInterval && null == pair && null == depthGroup) {
                    await this.getExchangeDataByInterval(this.provider.model.activePair, interval);
                    return;
                }

                await this.getExchangeData(pair, depthGroup, interval);
            }

            return;
        },
        createQueue() {
            if (this.provider.websocket.kline) {
                const data = [...this.provider.websocket.kline];
                this.provider.websocket.kline = [];
                this.provider.queue.kline = this.onKline(data);
            }

            if (this.provider.websocket.buy) {
                const data = [...this.provider.websocket.buy];
                this.provider.websocket.buy = [];
                this.provider.queue.buy = this.onDepth(data);
            }

            if (this.provider.websocket.sell) {
                const data = [...this.provider.websocket.sell];
                this.provider.websocket.sell = [];
                this.provider.queue.sell = this.onDepth(data);
            }

            if (this.provider.websocket.tradeHistory) {
                const data = [...this.provider.websocket.tradeHistory];
                this.provider.websocket.tradeHistory = [];
                this.provider.queue.tradeHistory = data;
            }
        },
        sync() {
            if (0 >= this.provider.websocket.kline.length && 0 >= this.provider.websocket.buy.length && 0 >= this.provider.websocket.sell.length && 0 >= this.provider.websocket.tradeHistory.length && !this.provider.recoveryMode) {
                return;
            }

            // replace old dataset with the synched data after recovery mode
            if (!this.provider.recoveryMode) {
                this.provider.data.kline = this.provider.kline;
                this.provider.data.tradeHistory = this.provider.tradeHistory;
            }

            if (!this.provider.lockSync) {
                this.provider.lockSync = true;

                const start = performance.now();

                this.createQueue();

                //latest data will always be at the bottom so reverse the array to make things simpler

                if (this.provider.recoveryMode) {
                    //only do this once because of the way trading view is designed
                    // new data will be pushed via this.provider.queue instead
                    if (0 < this.provider.queue.kline.length) {
                        const data = [...this.provider.data.kline];
                        this.provider.kline = this.syncKline([...this.provider.queue.kline], data);
                    }
                }

                if (0 < this.provider.queue.buy.length) {
                    const data = [...this.provider.data.buy];
                    const unsorted = this.syncDepth([...this.provider.queue.buy], data);
                    const sorted = this.sortBuyDepth([...unsorted]);
                    this.provider.data.buy = [...sorted];
                    this.provider.buy = this.limitBuyDepth([...sorted]);
                }

                if (0 < this.provider.queue.sell.length) {
                    const data = [...this.provider.data.sell];
                    const unsorted = this.syncDepth([...this.provider.queue.sell], data);
                    const sorted = this.sortSellDepth([...unsorted]);
                    this.provider.data.sell = [...sorted];
                    this.provider.sell = this.limitSellDepth([...sorted]);
                }

                if (0 < this.provider.queue.tradeHistory.length) {
                    const data = [...this.provider.data.tradeHistory];
                    this.provider.tradeHistory = this.syncTradeHistory([...this.provider.queue.tradeHistory], data).slice(0, this.provider.maxTradeHistoryRow);
                }

                if (this.provider.recoveryMode && !this.provider.requireSync && this.provider.apiReady) {
                    this.provider.recoveryMode = false;
                    // this.simulate();
                }

                const end = performance.now();

                setTimeout(() => {
                    this.provider.lockSync = false;
                    if (this.provider.requireSync) {
                        this.provider.requireSync = false;
                        this.sync();
                    }
                }, this.provider.throttle);

            } else {
                return;
            }
        },
        syncKline(queue, data) {
            //for checking if a timestamp has been processed
            let array = [];

            //for concat the new timestamp data into our response
            let newTimestamp = [];

            //the expected output
            let response = data;

            //when initial data is empty
            if (0 >= response.length) {
                for (let i = 0; i < queue.length; i++) {
                    const item = queue[i];

                    if (-1 == array.indexOf(item.timestamp)) {
                        array.push(item.timestamp);
                    } else {
                        continue;
                    }

                    response.push(item);
                }
            } else {
                for (let i = 0; i < queue.length; i++) {
                    const item = queue[i];

                    if (-1 == array.indexOf(item.timestamp)) {
                        array.push(item.timestamp);
                    } else {
                        continue;
                    }

                    if (item.timestamp == response[0].timestamp) {
                        response[0] = item;
                        continue;
                    }

                    if (item.timestamp > response[0].timestamp) {
                        newTimestamp.push(item);
                        continue;
                    }

                    if (item.timestamp < response[0].timestamp) {
                        break;
                    }
                }

                // if we have new timestamps we can merge existing data with the new ones
                if (0 < newTimestamp.length) {
                    response = newTimestamp.concat([...response]);
                }
            }

            return response;
        },
        syncDepth(queue, data) {
            let array = [];
            let response = data;

            // get required data only
            for (let i = 0; i < queue.length; i++) {
                const item = queue[i];
                if (-1 == array.indexOf(item.price)) {
                    array.push(item.price);

                    const volume = new BigNumber(item.volume);

                    if (0 < response.length) {
                        const index = response.findIndex(replacement => {
                            if (replacement.price == item.price) {
                                return item;
                            }
                        });

                        if (-1 != index) {
                            if (volume.isLessThanOrEqualTo(0)) {
                                response.splice(index, 1);
                                continue;
                            } else {
                                response[index].volume = item.volume;
                                response[index].amount = item.amount;
                            }
                        } else {
                            if (volume.isGreaterThan(0)) {
                                response.unshift(item);
                            }
                        }
                    } else {
                        if (volume.isGreaterThan(0)) {
                            response.unshift(item);
                        }
                    }
                }
            }

            return response;
        },
        syncTradeHistory(queue, data) {
            if (this.provider.maxTradeHistoryRow <= queue.length) {
                queue.splice(0, this.provider.maxTradeHistoryRow);
            }

            let response;

            if (this.provider.recoveryMode) {
                //search for exsiting data when under recorvery mode
                for (let i = 0; i < data.length; i++) {
                    const item = data[i];
                    const index = queue.findIndex(replacement => {
                        if (item.timestamp == replacement.timestamp && item.price == replacement.price && item.volume == replacement.volume && item.type == replacement.type) {
                            return item;
                        }
                    });

                    if (-1 != index) {
                        queue.splice(index, 1);
                    }

                    //if no more data then we can break the loop
                    if (0 >= queue.length) {
                        break;
                    }
                }

                if (0 < queue.length) {
                    response = queue.concat([...data]);
                } else {
                    response = [...data];
                }
            } else {
                //can just concat when not under recovery mode
                response = queue.concat([...data]);
            }

            return response;
        },
        onKline(value) {
            const response = value.map(item => {
                return {
                    timestamp: Number(item[0]),
                    open: Number(item[1]),
                    close: Number(item[2]),
                    high: Number(item[3]),
                    low: Number(item[4]),
                    volume: Number(item[5]),
                    amount: Number(item[6]),
                    change: Number(item[7]),
                    price: Number(item[8])
                };
            });

            return response;
        },
        onDepth(value) {
            const response = value.map(item => {
                return {
                    price: item[0],
                    volume: item[1],
                    amount: item[2]
                }
            });

            return response;
        },
        sortSellDepth(value) {
            let data = value;

            if (0 < data.length) {
                try {
                    let response = data.sort((a, b) => {
                        const price1 = new BigNumber(a.price);
                        const price2 = new BigNumber(b.price);

                        if (price1.isLessThan(price2)) {
                            return -1;
                        } else if (price1.isGreaterThan(price2)) {
                            return 1;
                        }

                        return 0;
                    });
                    return response;
                } catch (error) {
                    throw error;
                }
            } else {
                return [];
            }
        },
        sortBuyDepth(value) {
            let data = value;

            if (0 < data.length) {
                try {
                    let response = data.sort((a, b) => {
                        const price1 = new BigNumber(a.price);
                        const price2 = new BigNumber(b.price);

                        if (price1.isLessThan(price2)) {
                            return 1;
                        } else if (price1.isGreaterThan(price2)) {
                            return -1;
                        }

                        return 0;
                    });
                    return response;
                } catch (error) {
                    throw error;
                }
            } else {
                return [];
            }
        },
        limitSellDepth(value) {
            let data = value;

            if (0 < data.length) {
                try {
                    return data.slice(0, this.provider.maxDepthRow).reverse();
                } catch (error) {
                    throw error;
                }
            } else {
                return [];
            }
        },
        limitBuyDepth(value) {
            let data = value;

            if (0 < data.length) {
                try {
                    return data.slice(0, this.provider.maxDepthRow);
                } catch (error) {
                    throw error;
                }
            } else {
                return [];
            }
        },
        clearQueue() {
            this.provider.websocket.kline = [];
            this.provider.websocket.buy = [];
            this.provider.websocket.sell = [];
            this.provider.websocket.tradeHistory = [];
            this.provider.queue.kline = [];
            this.provider.queue.buy = [];
            this.provider.queue.sell = [];
            this.provider.queue.tradeHistory = [];
        },
        setDepthChart(data) {
            let totalVolume = new BigNumber(0);
            let response = [];

            try {
                for (let i = 0; i < data.length; i++) {
                    const item = data[i];
                    totalVolume = totalVolume.plus(new BigNumber(item.volume));
                    response.push([
                        Number(item.price),
                        totalVolume.toNumber()
                    ]);
                }

                return response;
            } catch (error) {
                throw error;
            }
        },
        getBestBuy() {
            let response = '';

            if (0 < this.provider.sell.length) {
                response = this.provider.sell[this.provider.sell.length - 1].price;
            }

            this.provider.bestBuy = response;
            return response;
        },
        getBestSell() {
            let response = '';

            if (0 < this.provider.buy.length) {
                response = this.provider.buy[0].price;
            }

            this.provider.bestSell = response;
            return response;
        },

        //simulation function
        simulate() {
            this.provider.recoveryMode = false;
            this.testKline();
            // this.testDepthBuy();
            // this.testDepthSell();
            // this.testTradeHistory();

            // setTimeout(() => {
            this.sync();
            // }, 1000);
        },
        testKline() {
            const timestamp = this.provider.kline[0].timestamp + 6000;
            const value = Math.random();
            for (let i = 0; i < 100; i++) {
                this.provider.websocket.kline.unshift([timestamp, value, value, value, value, value, value, value, value]);
            }
        },
        testDepthBuy() {
            this.provider.websocket.buy.unshift(["5095.00", "10", "10"]);
        },
        testDepthSell() {
            this.provider.websocket.sell.unshift(["6092.33", "99999", "999999"]);
        },
        testTradeHistory() {
            const timestamp = new Date().getTime() + 1585548600000;
            for (let i = 0; i < 100; i++) {
                this.provider.websocket.tradeHistory.unshift({
                    timestamp: timestamp,
                    price: '6664.61',
                    volume: '0.28792',
                    type: 1,
                });
            }
        }
    },
    data() {
        return {
            provider: {
                websocket: {
                    kline: [],
                    buy: [],
                    sell: [],
                    tradeHistory: [],
                },
                data: {
                    kline: [],
                    buy: [],
                    sell: [],
                    tradeHistory: [],
                },
                queue: {
                    kline: [],
                    buy: [],
                    sell: [],
                    tradeHistory: [],
                },
                kline: [],
                buy: [],
                sell: [],
                tradeHistory: [],
                buyChart: [],
                sellChart: [],
                bestBuy: '',
                bestSell: '',

                throttle: 500,
                apiReady: false,
                marketList: null,
                marketListGroup: null,
                activePair: null,
                activePairGroup: null,
                requireSync: false,
                lockSync: false,
                recoveryMode: true,
                ping: false,
                pong: false,
                maxTradeHistoryRow: (!window.env.maxTradeHistoryRow ? 20 : window.env.maxTradeHistoryRow),
                maxDepthRow: (!window.env.maxDepthRow ? 20 : window.env.maxDepthRow),
                model: {
                    activePair: null,
                    activeDepth: null,
                    activeInterval: null,
                    activePairGroup: null,
                },
            }
        }
    },
    beforeDestroy() {
        this.$socket.unsubscribe("setList");
        this.$socket.unsubscribe("getList");
        this.$socket.unsubscribe("marketList");
    }
}
